import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAllDiscountCodes } from "../../../../redux/reducers/clinicSlice";
import {
  getAllBillingRevenueReportData,
  getBillingRevenueReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import {
  PaymentTypeList,
  reportPaymentStatus,
  typeList,
} from "../../../../util/dropList";
import {
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  roundWithSignedDiff,
} from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./BillingRevenue.css";

const tableHdrs = [
  "invoiceNumber",
  "invoiceDate",
  "totalDue",
  "status",
  "amtPaid",
  "balanceDue",
  "discount",
  "discountCode",
  "taxes",
  "customerUserName",
  "petName",
  "vetName",
  "paymentMode",
  "type",
  "appointmentType",
];
const invoiceMap = [
  { key: "invoiceNumber", idType: "pharmacyDiscountId" },
  { key: "treatmentInvoiceNumber", idType: "treatmentDiscountId" },
  { key: "petShopInvoiceNumber", idType: "petShopDiscountId" },
];
const initSearch = {
  invoiceNumber: "",
  status: [],
  userName: "",
  petName: "",
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Invoice Date",
  "Total Due",
  "Amount Paid",
  "Balance Due",
  "Discount",
  "Discount Code",
  "Taxes",
  "Vet Name",
  "Payment Mode",
  "Type",
  "Appointment Type",
];
const dropFields = ["Discount Code", "Payment Mode", "Appointment Type"];

const BillingInvoicesTableReport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("2025-08 to 2026-08");
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { billingRevenue, billingRevenueAll } = useSelector(
    (state) => state?.report,
  );
  const { discountCodes } = useSelector((state) => state?.clinic);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllDiscountCodes());
    dispatch(getAllBillingRevenueReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    dateRange,
    selectedPeriod,
    searchValues?.invoiceNumber,
    searchValues?.status,
    searchValues?.userName,
    searchValues?.petName,
  ]);

  const overviewData = billingRevenue?.overview || billingRevenue?.data || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "Total sales",
      value:
        cards?.totalSalesDisplay ||
        (cards?.totalSales !== undefined ? `₹${(cards.totalSales / 100000).toFixed(1)}L` : "₹0L"),
    },
    {
      title: "Clinic share",
      value:
        cards?.clinicShareDisplay ||
        (cards?.clinicShare !== undefined ? `${cards.clinicShare}%` : "0%"),
    },
    {
      title: "Pet shop share",
      value:
        cards?.petShopShareDisplay ||
        (cards?.petShopShare !== undefined ? `${cards.petShopShare}%` : "0%"),
    },
    {
      title: "Top category",
      value: cards?.topCategory || "—",
    },
  ];

  // Chart 1: Revenue trend, by source (Multi-line)
  const rawTrend = Array.isArray(overviewData?.revenueTrendBySource)
    ? overviewData.revenueTrendBySource
    : [];

  const trendCategories =
    rawTrend.length > 0
      ? rawTrend.map((item) => item?.month || "")
      : ["Aug 25", "Sep 25", "Oct 25", "Nov 25", "Dec 25", "Jan 26", "Feb 26", "Mar 26", "Apr 26", "May 26", "Jun 26", "Jul 26", "Aug 26"];

  const clinicTrend =
    rawTrend.length > 0
      ? rawTrend.map((item) => (typeof item?.clinic === "number" && !isNaN(item.clinic) ? item.clinic : 0))
      : [0, 1000, 139278, 68819, 9797, 6852, 88664, 308632, 567596, 235481, 78277, 169365, 63729];

  const pharmacyTrend =
    rawTrend.length > 0
      ? rawTrend.map((item) => (typeof item?.pharmacy === "number" && !isNaN(item.pharmacy) ? item.pharmacy : 0))
      : [0, 0, 0, 127, 0, 0, 7200, 9504, 32265, 30712, 5509, 2503, 182];

  const petShopTrend =
    rawTrend.length > 0
      ? rawTrend.map((item) => (typeof item?.petShop === "number" && !isNaN(item.petShop) ? item.petShop : 0))
      : [0, 0, 288, 2324, 0, 0, 0, 3718, 18650, 11000, 700, 1670, 0];

  const revenueTrendChartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    colors: ["#6366F1", "#F97316", "#10B981"],
    xaxis: {
      categories: trendCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => val >= 100000 ? `${(val / 100000).toFixed(1)}L` : `${Math.round(val / 1000)}k`,
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    legend: { position: "top", horizontalAlign: "left", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const revenueTrendChartSeries = [
    { name: "Clinic", data: clinicTrend },
    { name: "Pharmacy", data: pharmacyTrend },
    { name: "Pet Shop", data: petShopTrend },
  ];

  // Chart 2: Revenue by category (Bar Chart)
  const rawCategories = Array.isArray(overviewData?.revenueByCategory)
    ? overviewData.revenueByCategory
    : [];

  const categoryNames =
    rawCategories.length > 0
      ? rawCategories.map((item) => item?.category || "Other")
      : ["Consultation", "Lab Tests", "Grooming", "Vaccination", "Others", "Surgery"];

  const categoryValues =
    rawCategories.length > 0
      ? rawCategories.map((item) => (typeof item?.revenue === "number" && !isNaN(item.revenue) ? item.revenue : 0))
      : [8616307, 26000, 24955, 12475, 2850, 421];

  const categoryChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "40%", borderRadius: 3 } },
    colors: ["#2563EB"],
    xaxis: {
      categories: categoryNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `${(val / 1000).toFixed(0)}k` : `${val}`,
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const categoryChartSeries = [
    { name: "Revenue", data: categoryValues },
  ];

  // Chart 3: Revenue by source (Donut Chart)
  const rawSources = Array.isArray(overviewData?.revenueBySource)
    ? overviewData.revenueBySource
    : [];

  const sourceLabels =
    rawSources.length > 0
      ? rawSources.map((item) => item?.source || "Other")
      : ["Clinic", "Pharmacy", "Pet Shop", "Others"];

  const sourceSeries =
    rawSources.length > 0
      ? rawSources.map((item) => (typeof item?.percentage === "number" && !isNaN(item.percentage) ? item.percentage : typeof item?.revenue === "number" && !isNaN(item.revenue) ? item.revenue : 0))
      : [93.2, 4.7, 2.1, 0];

  const sourceChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: sourceLabels,
    colors: ["#0F172A", "#2563EB", "#60A5FA", "#CBD5E1"],
    plotOptions: { pie: { donut: { size: "60%" } } },
    dataLabels: { enabled: false },
    legend: { position: "bottom", horizontalAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
  };

  const sourceChartSeries = sourceSeries;

  // Chart 4: Vet-wise billing contribution (Bar Chart)
  const rawVets = Array.isArray(overviewData?.vetWiseBilling)
    ? overviewData.vetWiseBilling
    : [];

  const vetNames =
    rawVets.length > 0
      ? rawVets.map((item) => item?.doctorName || item?.vetName || "Vet")
      : ["Vivek", "Ranjan", "Sashvanth", "Supervet", "Vetinstant"];

  const vetValues =
    rawVets.length > 0
      ? rawVets.map((item) => (typeof item?.revenue === "number" && !isNaN(item.revenue) ? item.revenue : 0))
      : [1179724, 100101, 50682, 47765, 36470];

  const vetContributionChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "35%", borderRadius: 3 } },
    colors: ["#2563EB"],
    xaxis: {
      categories: vetNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `${(val / 1000).toFixed(0)}k` : `${val}`,
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const vetContributionChartSeries = [
    { name: "Revenue", data: vetValues },
  ];

  const handleExportExcel = () => {
    const tableData = billingRevenue?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Invoice No.": row?.invoiceNumber ?? "",
      "Invoice Date": row?.invoiceDate ? moment(new Date(row?.invoiceDate)).format("DD MMM YYYY") : "",
      "Total Due": row?.totalDue ?? 0,
      Status: row?.status ?? "",
      "Amount Paid": row?.amtPaid ?? 0,
      "Balance Due": row?.balanceDue ?? 0,
      "Vet Name": row?.vetName ?? "",
      "Pet Name": row?.petName ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue Report");
    XLSX.writeFile(workbook, `Revenue_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getDatesFromRange = (rangeStr) => {
    if (!rangeStr) return { startDate: "2025-08-01", endDate: "2026-08-31" };
    const parts = rangeStr.split(" to ");
    if (parts.length === 2) {
      const start = moment(parts[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      const end = moment(parts[1], "YYYY-MM").endOf("month").format("YYYY-MM-DD");
      return { startDate: start, endDate: end };
    }
    return { startDate: "2025-08-01", endDate: "2026-08-31" };
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const { startDate, endDate } = getDatesFromRange(dateRange);
    const reqObj = {
      page: currentPage,
      itemSize: rowsPerPage,
      startDate,
      endDate,
      period: selectedPeriod,
      topN: 10,
    };
    if (reset) return reqObj;
    if (searchValues?.invoiceNumber?.length > 0) reqObj.invoiceNumber = [searchValues?.invoiceNumber];
    if (searchValues?.status?.length > 0) reqObj.status = searchValues?.status?.map((li) => li?.value);
    if (searchValues?.userName?.length > 0) reqObj.userName = [searchValues?.userName];
    if (searchValues?.petName?.length > 0) reqObj.petName = [searchValues?.petName];
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getBillingRevenueReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((billingRevenue?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getAllList = () => {
    const reqList = (billingRevenue?.data ?? [])?.flatMap((item) =>
      invoiceMap
        ?.filter(({ key }) => item[key]?.length > 0)
        ?.map(({ key, idType }) => {
          const billList =
            key === "treatmentInvoiceNumber"
              ? getTreatmentRecordBasedType(item?.paymentDetails)
              : key === "invoiceNumber"
                ? getPharmacyRecordBasedType(item?.paymentDetails)
                : key === "petShopInvoiceNumber"
                  ? getPetShopRecordBasedType(item?.paymentDetails)
                  : [];
          return {
            billList,
            invoiceNumber: item?.[key] ?? "",
            usedDicountCode: item?.[idType] ?? [],
            invoiceDate: item?.updatedDate ?? item?.createdDate,
            status: item?.status,
            amtPaid: item?.amtPaid,
            balanceDue: item?.balanceDue,
            customerUserName: item?.userName,
            petName: item?.petName,
            vetName: item?.doctorName,
            paymentMode: item?.paymentMode,
            type: item?.type,
            appointmentType:
              item?.appointmentType === "Home"
                ? "Home Visit"
                : item?.appointmentType,
          };
        }),
    );
    return reqList;
  };

  const getTableData = () => {
    return (getAllList() ?? [])?.map((li) => {
      const reqDiscountCodes = (li?.usedDicountCode ?? [])?.map((nl) => {
        const reqCode = discountCodes?.find(
          (dc) => (dc?.billId ?? "")?.toString() === nl,
        );
        if (reqCode) return reqCode?.discountCode ?? "";
        return "";
      });
      const amtDetail = getTaxAndSubTotalAndTotal(li?.billList);
      const totalDue = Number(
        roundWithSignedDiff(li?.balanceDue ?? 0)?.rounded,
      )?.toFixed(2);
      const discount = Number(
        roundWithSignedDiff(amtDetail?.discount ?? 0)?.rounded,
      )?.toFixed(2);
      const taxes = Number(
        roundWithSignedDiff(amtDetail?.tax ?? 0)?.rounded,
      )?.toFixed(2);
      return {
        ...li,
        totalDue,
        discount,
        taxes,
        discountCode:
          reqDiscountCodes?.length > 0 ? reqDiscountCodes?.join(", ") : null,
        invoiceDate: li?.invoiceDate
          ? moment(new Date(li?.invoiceDate)).format("DD MMM YYYY")
          : "",
        amtPaid: Number(li?.amtPaid ?? 0)?.toFixed(2),
        balanceDue: Number(li?.balanceDue ?? 0)?.toFixed(2),
      };
    });
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getDropListByType = (typ) => {
    return typ === "Payment Mode"
      ? PaymentTypeList
      : typ === "Appointment Type"
        ? typeList
        : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="billing-revenue-container">
      {/* Title */}
      <h2 className="billing-revenue-title">Revenue report</h2>

      {/* Top Filter Bar */}
      <div className="billing-revenue-filter-bar">
        <Select
          size="small"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="2025-08 to 2026-08">2025-08 to 2026-08</MenuItem>
          <MenuItem value="2026-01 to 2026-08">2026-01 to 2026-08</MenuItem>
        </Select>

        <div className="billing-revenue-period-group">
          {[
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
            { label: "Year", value: "year" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`billing-revenue-period-btn ${selectedPeriod === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="billing-revenue-export-group">
        <button type="button" className="billing-revenue-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="billing-revenue-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (4 Cards) */}
      <div className="billing-revenue-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="billing-revenue-card" key={idx}>
            <h4 className="billing-revenue-card-title">{card.title}</h4>
            <div className="billing-revenue-card-value-container">
              <h2 className="billing-revenue-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Line Chart: Revenue trend by source */}
      <div className="billing-revenue-chart-card">
        <div className="billing-revenue-chart-header">
          Revenue trend, by source
        </div>
        <Chart
          options={revenueTrendChartOptions}
          series={revenueTrendChartSeries}
          type="line"
          height={300}
        />
      </div>

      {/* Side-by-side Grid: Category Bar Chart + Source Donut Chart */}
      <div className="billing-revenue-charts-grid">
        <div className="billing-revenue-chart-card">
          <div className="billing-revenue-chart-header">
            Revenue by category
          </div>
          <Chart
            options={categoryChartOptions}
            series={categoryChartSeries}
            type="bar"
            height={280}
          />
        </div>

        <div className="billing-revenue-chart-card">
          <div className="billing-revenue-chart-header">
            Revenue by source
          </div>
          <Chart
            options={sourceChartOptions}
            series={sourceChartSeries}
            type="donut"
            height={280}
          />
        </div>
      </div>

      {/* Full-width Bar Chart: Vet-wise billing contribution */}
      <div className="billing-revenue-chart-card">
        <div className="billing-revenue-chart-header">
          Vet-wise billing contribution
        </div>
        <Chart
          options={vetContributionChartOptions}
          series={vetContributionChartSeries}
          type="bar"
          height={300}
        />
      </div>

      {/* Drill-down Section */}
      <div className="billing-revenue-drilldown-header">Drill-down — invoices</div>

      {/* Filter Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              value={searchValues?.invoiceNumber ?? ""}
              name="invoiceNumber"
              handleChange={onSearchChange}
              label="Invoice No."
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={reportPaymentStatus}
              value={searchValues?.status ?? []}
              name="status"
              handleChange={onSearchChange}
              label="Status"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              value={searchValues?.userName ?? ""}
              name="userName"
              handleChange={onSearchChange}
              label="Customer Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              value={searchValues?.petName ?? ""}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {billingRevenue?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((billingRevenue?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default BillingInvoicesTableReport;
